import { isFreePlan } from "./planConstants.ts";

/** Every plan on this page is sold annually. Monthly billing is not offered. */
export const BILLING_CYCLE = "Yearly" as const;
export const MONTHS_PER_TERM = 12;

export interface Plan {
  planName: string;
  amountMonth: number;
  discountAnnual: number;
  discountMonth: number;
  noOfStocks: number;
  channel: string;
  features: string[];
}

/** Plans the catalog returns but the pricing page never sells: Trial and Life Time Free. */
export const isPurchasablePlan = (plan: Plan): boolean =>
  !isFreePlan(plan.planName) && plan.amountMonth > 0;

/** Catalog order is arbitrary, so ladder the cards cheapest -> priciest. */
export const sortPlansByPrice = (plans: Plan[]): Plan[] =>
  [...plans].sort((a, b) => a.amountMonth - b.amountMonth);

/** "Legend150" reads as a SKU; "Legend 150" reads as a plan. Display only. */
export const formatPlanName = (planName: string): string =>
  planName.replace(/([A-Za-z])(\d)/g, "$1 $2");

export const formatMoney = (amount: number): string =>
  `₹${Math.round(amount).toLocaleString("en-IN")}`;

export interface AnnualPrice {
  /** The number that gets the big type - the effective cost per month. */
  headlineMonthly: number;
  /** Catalog per-month price, shown struck through. Null when nothing is off. */
  strikeMonthly: number | null;
  /** What the customer is actually charged for the year. */
  billedTotal: number;
  /** Undiscounted charge for the year. */
  listTotal: number;
  /** listTotal - billedTotal, over the year. */
  savings: number;
  /** Rounded, derived from the amounts so coupons are reflected too. */
  savingsPercent: number;
}

const round = (value: number): number => Math.round(value);

/**
 * Everything the card renders, derived from the catalog row.
 *
 * The headline is per-month - a monthly figure is the number a customer can judge -
 * while the real once-a-year charge is disclosed alongside it.
 * `couponTotal` is the server's final amount for the year when a coupon is on.
 */
export const getAnnualPrice = (
  plan: Plan,
  couponTotal?: number | null
): AnnualPrice => {
  const listTotal = round(plan.amountMonth * MONTHS_PER_TERM);
  const discount = plan.discountAnnual || 0;
  const discountedTotal = round(listTotal - (listTotal * discount) / 100);
  const billedTotal = couponTotal != null ? round(couponTotal) : discountedTotal;
  const savings = Math.max(listTotal - billedTotal, 0);

  return {
    headlineMonthly: round(billedTotal / MONTHS_PER_TERM),
    strikeMonthly: savings > 0 ? plan.amountMonth : null,
    billedTotal,
    listTotal,
    savings,
    savingsPercent: listTotal > 0 ? round((savings / listTotal) * 100) : 0,
  };
};

export const maxAnnualDiscount = (plans: Plan[]): number =>
  plans.reduce((max, plan) => Math.max(max, plan.discountAnnual || 0), 0);

/**
 * Card order and which one we push.
 *
 * The flagship - the priciest plan - is the one we recommend, and it sits second
 * rather than last so it is seen before the eye settles on a cheaper tier. The rest
 * keep their cheapest-first ladder.
 */
export const arrangePlansForDisplay = (
  plans: Plan[]
): { ordered: Plan[]; recommendedIndex: number } => {
  const ladder = sortPlansByPrice(plans);
  if (ladder.length < 2) {
    return { ordered: ladder, recommendedIndex: ladder.length ? 0 : -1 };
  }
  const flagship = ladder.pop() as Plan;
  ladder.splice(1, 0, flagship);
  return { ordered: ladder, recommendedIndex: 1 };
};
